import React from "react";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";
import { toast } from "react-toastify";
import api from "../api/axios";

const Renew = ({ data, onClose }) => {
  if (!data) return null;

  const plan = data.subscription || {};
  const customer = data.customer_details || {};

  const status = data.status || "UNKNOWN";
  const planType = (plan?.plan_type || "").toUpperCase();

  /* ================= PRICE LOGIC =================
     plan.price is FINAL PRICE including 18% GST
  ================================================= */
  const total = Number(plan?.price || 0);
  const subtotal = Number((total / 1.18).toFixed(2));
  const taxes = Number((total - subtotal).toFixed(2));

  const formatCurrency = (value) => `₹ ${Number(value || 0).toFixed(2)}`;

  const formatDate = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  /* ================= PAYMENT ================= */
  const handlePayment = async () => {
    try {
      if (status === "ACTIVE") {
        toast.info("Your subscription is already active");
        return;
      }

      if (status !== "HALTED" && status !== "PENDING") {
        toast.warn("Subscription cannot be renewed");
        return;
      }

      toast.info("Generating payment link...");

      const { data: response } = await api.post(
        `/paymentrzrrts/subscriptions/${data.customer_subscription_id}/repayment-link`
      );

      if (!response?.success) {
        toast.error(response?.message || "Failed to generate repayment link");
        return;
      }

      const shortUrl = response?.data?.short_url;

      if (!shortUrl) {
        toast.error("Repayment link not available");
        return;
      }

      toast.success("Redirecting to payment page...");

      window.open(shortUrl, "_blank", "noopener,noreferrer");

      onClose?.();
    } catch (err) {
      console.error("Repayment link error:", err);

      toast.error(
        err.response?.data?.message || "Failed to start repayment flow"
      );
    }
  };

  /* ================= STATUS ================= */
  const getStatusMessage = () => {
    switch (status) {
      case "ACTIVE":
        return "✅ Your subscription is active and running.";
      case "PENDING":
        return "⚠️ Renewal payment is pending. Please complete payment to continue uninterrupted service.";
      case "HALTED":
        return "❌ Subscription is halted. Please update payment and reactivate access.";
      case "CANCELLED":
        return "🚫 Subscription cancelled.";
      case "COMPLETED":
        return "🎉 Subscription completed. Please purchase a new plan.";
      case "EXPIRED":
        return "⌛ Subscription expired.";
      default:
        return "ℹ️ Subscription status unknown.";
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case "ACTIVE":
        return "#1b5e20";
      case "PENDING":
        return "#7c4d00";
      case "HALTED":
        return "#b71c1c";
      case "CANCELLED":
        return "#424242";
      case "COMPLETED":
        return "#0d47a1";
      case "EXPIRED":
        return "#5d4037";
      default:
        return "#2d2c2c";
    }
  };

  const getActionLabel = () => {
    if (status === "ACTIVE") return "Already Active";
    if (status === "PENDING") return "Complete Payment";
    if (status === "HALTED") return "Reactivate Subscription";
    return "Continue";
  };

  return (
    <div className="p-0 userDashtab">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h3 className="fw-bold mb-1">
          <strong className="text-warning">{plan?.sub_name || "Subscription"} Plan</strong>
        </h3>
      </div>

      <p className="text-muted mb-4" style={{ fontSize: "14px" }}>
        Review your subscription and proceed to payment
      </p>

      {status && (
        <div
          className="mb-3 p-3"
          style={{
            borderRadius: "8px",
            fontWeight: 500,
            backgroundColor: getStatusColor(),
            color: "#fff",
          }}
        >
          {getStatusMessage()}
        </div>
      )}

      <div className="mb-3">
        <h6 className="text-secondary">Customer Details</h6>

        <div className="small">
          📞 {customer?.phone_no || "-"} <br />
          📍 {customer?.address1 || "-"}
          {customer?.city ? `, ${customer.city}` : ""}
          {customer?.state ? `, ${customer.state}` : ""} <br />
          🌍 {customer?.country || "-"} {customer?.zipcode ? `- ${customer.zipcode}` : ""}
        </div>
      </div>

      <div className="mb-3">
        <div className="d-flex justify-content-between">
          <span>Plan Type</span>
          <strong>{planType || "-"}</strong>
        </div>

        <div className="d-flex justify-content-between">
          <span>Credits</span>
          <strong>{plan?.credits_per_month ?? "-"}</strong>
        </div>

        <div className="d-flex justify-content-between">
          <span>Next Billing</span>
          <strong>{formatDate(data?.renewal_date)}</strong>
        </div>

        <div className="d-flex justify-content-between">
          <span>Expires On</span>
          <strong>{formatDate(data?.end_date)}</strong>
        </div>

        <div className="d-flex justify-content-between">
          <span>Auto Renew</span>
          <strong>{data?.auto_renew ? "Enabled" : "Disabled"}</strong>
        </div>
      </div>

      <hr />

      <div className="d-flex justify-content-between">
        <span>Price (Excl. GST)</span>
        <span>{formatCurrency(subtotal)}</span>
      </div>

      <div className="d-flex justify-content-between mb-3">
        <span className="d-flex align-items-center gap-2">
          GST (18%)
          <i
            className="pi pi-question-circle"
            data-pr-tooltip="GST is already included in the total amount"
            style={{ cursor: "pointer" }}
          />
          <Tooltip target=".pi-question-circle" />
        </span>

        <span>{formatCurrency(taxes)}</span>
      </div>

      <hr />

      <div className="d-flex justify-content-between mb-2">
        <h5>Total</h5>
        <h5>
          <strong>{formatCurrency(total)}</strong>
        </h5>
      </div>

      <div className="d-flex justify-content-end gap-2">
        <Button
          label="Cancel"
          severity="secondary"
          outlined
          onClick={onClose}
        />

        <Button
          label={getActionLabel()}
          severity="warning"
          disabled={status === "ACTIVE"}
          onClick={handlePayment}
        />
      </div>
    </div>
  );
};

export default Renew;